import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone } from '@jack-henry/frontend-utils/functions';
import { DashboardService } from '@treasury/domain/dashboard';
import { DashboardWidget } from '@treasury/domain/dashboard/dashboard-widget.entity';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './add-widget-menu';
import './order-widgets-table';

export enum PwaAvailableWidget {
    Accounts = 'Accounts',
    InformationCenter = 'Information Center',
    Resources = 'Resources',
    QuickTransfer = 'Quick Transfer',
    FavoriteReports = 'Favorite Reports',
    StopPaymentPendingApproval = 'Stop Payment Pending Approval',
    PositivePay = 'Positive Pay',
    PaymentsPendingApproval = 'Payments Pending Approval',
    QuickLoanPayment = 'Quick Loan Payment',
    CheckLookup = 'Check Lookup',
}

export const pwaAvailableWidgets = [
    PwaAvailableWidget.Accounts,
    PwaAvailableWidget.InformationCenter,
    PwaAvailableWidget.Resources,
    PwaAvailableWidget.QuickTransfer,
    PwaAvailableWidget.FavoriteReports,
    // PwaAvailableWidget.StopPaymentPendingApproval,
    PwaAvailableWidget.PositivePay,
    PwaAvailableWidget.PaymentsPendingApproval,
    PwaAvailableWidget.QuickLoanPayment,
    PwaAvailableWidget.CheckLookup,
];

export const tagName = 'configure-widgets-sheet';

@customElement(tagName)
export class ConfigureWidgetsSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare private featureFlagService: Promise<FeatureFlagService>;

    @InjectProperty()
    declare private dashboardService: DashboardService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private deviceService: DeviceService;

    @state()
    private loading = false;

    @state()
    private hasConfigureDashboard = false;

    @state()
    private hasCheckLookup = false;

    @state()
    private hasQuickTransfer = false;

    @state()
    private configuredWidgets: DashboardWidget[] = [];

    @state()
    private initialConfiguredWidgets: DashboardWidget[] = [];

    @state()
    private availableWidgets: DashboardWidget[] = [];

    @state()
    private hasNoConfigurationUpdates = true;

    protected async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('open') && this.open) {
            await this.loadWidgets();
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            horizontal
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center" class="flex flex-col">
                <span>Organize Dashboard</span><span class="text-xs">Drag & drop to reorder</span>
            </div>
            <div slot="header-right">
                ${this.loading
                    ? nothing
                    : html`<add-widget-menu
                          .availableWidgets=${this.availableWidgets}
                          @addWidgets=${(e: CustomEvent<DashboardWidget[]>) => {
                              this.availableWidgets = this.availableWidgets.filter(
                                  widget =>
                                      !e.detail.find(addedWidget => addedWidget.id === widget.id)
                              );
                              const previousLastWidget =
                                  this.configuredWidgets[this.configuredWidgets.length - 1];
                              e.detail.forEach((widget, index) => {
                                  widget.left = previousLastWidget?.left || 0;
                                  widget.top = previousLastWidget?.top + index++;
                              });
                              this.configuredWidgets = [...this.configuredWidgets, ...e.detail];
                              this.checkConfigurationUpdates();
                          }}
                      ></add-widget-menu>`}
            </div>
            <div id="order-widgets-table" class="mt-5 px-2">
                ${this.renderBlockingLoader()}
                ${this.loading
                    ? html`<tm-loader card></tm-loader>`
                    : html`<order-widgets-table
                          .configuredWidgets=${this.configuredWidgets}
                          .isDesktop=${this.deviceService.isDesktop}
                          @widgets-reordered=${(e: CustomEvent<DashboardWidget[]>) => {
                              this.configuredWidgets = e.detail;
                              this.checkConfigurationUpdates();
                          }}
                          @widget-removed=${(
                              e: CustomEvent<{
                                  widget: DashboardWidget;
                                  configuredWidgets: DashboardWidget[];
                              }>
                          ) => {
                              const { widget, configuredWidgets } = e.detail;
                              this.configuredWidgets = [...configuredWidgets];
                              this.availableWidgets = [...this.availableWidgets, widget];
                              this.checkConfigurationUpdates();
                          }}
                      ></order-widgets-table>`}
            </div>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Save',
                        onClick: async () => await this.setConfiguredWidgets(),
                        disabled: this.loading || this.hasNoConfigurationUpdates,
                    },
                ]}
            ></tm-footer
        ></tm-bottom-sheet>`;
    }

    private async checkFeatureFlag() {
        const featureFlagService = await this.featureFlagService;
        this.hasConfigureDashboard = await featureFlagService.isEnabled(
            Feature.PwaConfigureDashboard
        );
        this.hasCheckLookup = await featureFlagService.isEnabled(Feature.CheckLookupWidget);
        this.hasQuickTransfer = await featureFlagService.isEnabled(
            Feature.PwaTransferPaymentWidget
        );
        if (!this.hasCheckLookup || !this.deviceService.isDesktop) {
            if (pwaAvailableWidgets.includes(PwaAvailableWidget.CheckLookup)) {
                pwaAvailableWidgets.splice(
                    pwaAvailableWidgets.indexOf(PwaAvailableWidget.CheckLookup),
                    1
                );
            }
        }
        if (!this.hasQuickTransfer || !this.deviceService.isDesktop) {
            if (pwaAvailableWidgets.includes(PwaAvailableWidget.QuickTransfer)) {
                pwaAvailableWidgets.splice(
                    pwaAvailableWidgets.indexOf(PwaAvailableWidget.QuickTransfer),
                    1
                );
            }
        }
        if (!this.hasConfigureDashboard) {
            this.notificationService.renderError(
                'You do not have permission to configure the dashboard. Please contact your administrator.'
            );
            this.open = false;
            return;
        }
    }

    private async loadWidgets() {
        this.loading = true;
        await this.checkFeatureFlag();
        try {
            const [configuredWidgets, availableWidgets] = await Promise.all([
                this.deviceService.isDesktop
                    ? this.dashboardService.getConfiguredWidgets()
                    : this.dashboardService.getConfiguredMobileWidgets(),
                this.deviceService.isDesktop
                    ? this.dashboardService.getAvailableWidgets()
                    : this.dashboardService.getAvailableMobileWidgets(),
            ]);
            this.configuredWidgets = configuredWidgets;
            // check for desktop and remove widgets that are not available on mobile
            if (!this.deviceService.isDesktop) {
                // check lookup and transfer widgets are not available on mobile
                this.configuredWidgets = this.configuredWidgets.filter(
                    widget =>
                        ![
                            PwaAvailableWidget.CheckLookup,
                            PwaAvailableWidget.QuickTransfer,
                        ].includes(widget.title as PwaAvailableWidget)
                );
            }
            this.initialConfiguredWidgets = clone(this.configuredWidgets);
            // filter out widgets that are turned off in back office
            // filter out configured widgets from available widgets
            this.availableWidgets = availableWidgets
                .filter(widget => widget.hasPermission)
                .filter(
                    widget =>
                        !this.configuredWidgets.find(
                            configuredWidget => configuredWidget.id === widget.id
                        )
                )
                .filter(widget => pwaAvailableWidgets.includes(widget.title as PwaAvailableWidget));
            this.checkConfigurationUpdates();
        } catch {
            this.notificationService.renderError('Failed to load dashboard widgets');
        } finally {
            this.loading = false;
        }
    }

    private checkConfigurationUpdates() {
        if (this.configuredWidgets.length !== this.initialConfiguredWidgets.length) {
            this.hasNoConfigurationUpdates = false;
            return;
        }
        for (let i = 0; i < this.configuredWidgets.length; i++) {
            if (this.configuredWidgets[i].type !== this.initialConfiguredWidgets[i].type) {
                this.hasNoConfigurationUpdates = false;
                return;
            } else {
                this.hasNoConfigurationUpdates = true;
            }
        }
    }

    private async setConfiguredWidgets() {
        this.loading = true;
        try {
            if (this.deviceService.isDesktop) {
                await this.dashboardService.setConfiguredWidgets(this.configuredWidgets);
            } else {
                await this.dashboardService.setConfiguredMobileWidgets(this.configuredWidgets);
            }

            this.notificationService.renderSuccess('Dashboard configuration saved');
            this.dispatchEvent(new CustomEvent('dashboard-configured'));
            this.hasNoConfigurationUpdates = true;
            this.loadWidgets();
        } catch {
            this.notificationService.renderError('Failed to save dashboard configuration');
        } finally {
            this.loading = false;
        }
    }

    private renderBlockingLoader() {
        if (this.loading) {
            return html`<tm-blocker-loader></tm-blocker-loader>`;
        }
        return nothing;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ConfigureWidgetsSheet;
    }
}
